// Phase 9.4.B — Portal invoices list (2026-05-11 #3).
//
// Spec: docs/apps/client-portal.md § "Invoices".
//
// Columns: invoice #, project, date, total, balance, status pill, aging
// pill. Filter chips: Open vs Paid (the two most common slices). Click
// row → /invoices/:id for detail + Pay Now flow.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  type InvoiceStatus,
  type PortalInvoiceListResult,
  BUCKET_LABEL,
  BUCKET_PILL_CLASS,
  STATUS_LABEL,
  STATUS_PILL_CLASS,
  fetchPortalInvoices,
  formatDate,
  formatUsd,
} from "@/lib/portal-invoices";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

type StatusFilter = "all" | "open" | "paid";

const OPEN_STATUSES: InvoiceStatus[] = ["open", "sent_to_client", "exported"];
const PAID_STATUSES: InvoiceStatus[] = ["paid"];

function statusesForFilter(f: StatusFilter): InvoiceStatus[] | undefined {
  if (f === "all") return undefined;
  if (f === "open") return OPEN_STATUSES;
  return PAID_STATUSES;
}

export function InvoicesListPage() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [pages, setPages] = useState<PortalInvoiceListResult[]>([]);

  const filters = {
    status: statusesForFilter(statusFilter),
    limit: 20,
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["portal-invoices", statusFilter],
    queryFn: () => fetchPortalInvoices(filters),
  });

  function setFilter(f: StatusFilter): void {
    setPages([]);
    setStatusFilter(f);
  }

  async function loadMore(): Promise<void> {
    const cursor = (pages[pages.length - 1] ?? data)?.nextCursor;
    if (!cursor) return;
    const next = await fetchPortalInvoices({ ...filters, cursor });
    setPages((prev) => [...prev, next]);
  }

  const allRows = [...(data?.rows ?? []), ...pages.flatMap((p) => p.rows)];
  const hasMore = (pages[pages.length - 1] ?? data)?.nextCursor != null;
  if (!user) return null;

  // Compute outstanding-balance summary across the visible rows. Rough
  // — the server returns paginated results, so this is "outstanding in
  // the loaded set." For full accuracy we'd need a dedicated summary
  // endpoint; the current 20-row default covers ~all clients at launch.
  const outstandingTotal = allRows
    .filter((r) => OPEN_STATUSES.includes(r.status))
    .reduce((sum, r) => sum + Number.parseFloat(r.balanceDue || "0"), 0);

  return (
    <PortalLayout>
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <Link
              to="/dashboard"
              className="mb-3 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
            >
              ← Dashboard
            </Link>
            <h1 className="font-display text-4xl font-bold tracking-tight">
              Invoices
            </h1>
            <p className="mt-2 text-brown-400">
              View and pay your invoices, and see what you've paid.
            </p>
          </div>
          {outstandingTotal > 0 && statusFilter !== "paid" && (
            <div className="text-right">
              <div className="text-xs font-semibold uppercase tracking-wider text-brown-300">
                Outstanding
              </div>
              <div className="font-display text-3xl font-bold tracking-tight text-rust-500 tabular-nums">
                {formatUsd(outstandingTotal.toFixed(2))}
              </div>
            </div>
          )}
        </div>

        {/* Filter chips */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          {(["all", "open", "paid"] as const).map((f) => {
            const active = statusFilter === f;
            const label = f === "all" ? "All" : f === "open" ? "Open" : "Paid";
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={
                  "rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors " +
                  (active
                    ? "border-rust-400 bg-ink text-white"
                    : "border-cream-300 bg-white text-brown-400 hover:border-brown-300 hover:text-brown-500")
                }
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
          {isLoading && (
            <div className="px-6 py-16 text-center text-brown-300">
              Loading your invoices…
            </div>
          )}
          {isError && (
            <div className="px-6 py-12 text-center">
              <p className="mb-3 text-rust-500">
                We couldn't load your invoices.
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="text-sm font-semibold text-ink underline-offset-2 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
          {!isLoading && !isError && allRows.length === 0 && (
            <div className="px-6 py-16 text-center">
              <p className="mb-2 font-display text-2xl font-bold tracking-tight text-brown-500">
                {statusFilter === "open"
                  ? "No open invoices"
                  : statusFilter === "paid"
                    ? "No paid invoices yet"
                    : "No invoices yet"}
              </p>
              <p className="text-brown-400">
                {statusFilter === "all"
                  ? "Your invoices will appear here once your account manager generates them."
                  : "Try the All filter to see everything."}
              </p>
            </div>
          )}
          {allRows.length > 0 && (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
                  <th className="px-6 py-4">Invoice #</th>
                  <th className="px-6 py-4">Project</th>
                  <th className="px-6 py-4">Dated</th>
                  <th className="px-6 py-4 text-right">Total</th>
                  <th className="px-6 py-4 text-right">Balance</th>
                  <th className="px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {allRows.map((row) => (
                  <tr
                    key={row.id}
                    className="group border-b border-cream-100 last:border-b-0 transition-colors hover:bg-cream-50"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={`/invoices/${row.id}`}
                        className="font-semibold text-ink underline-offset-2 group-hover:underline"
                      >
                        #{row.invoiceNumber}
                      </Link>
                      <div className="mt-0.5 text-xs text-brown-300">
                        {row.invoiceType === "deposit" ? "Invoice · Prepay" : "Invoice"} ·
                        Order #{row.orderNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-brown-500">
                      {row.projectName}
                    </td>
                    <td className="px-6 py-4 text-sm text-brown-400">
                      {formatDate(row.invoiceDate)}
                    </td>
                    <td className="px-6 py-4 text-right text-brown-500 tabular-nums">
                      {formatUsd(row.total)}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold tabular-nums">
                      <span
                        className={
                          row.status === "paid" || row.status === "voided"
                            ? "text-brown-300"
                            : "text-brown-500"
                        }
                      >
                        {formatUsd(row.balanceDue)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={
                            "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold " +
                            STATUS_PILL_CLASS[row.status]
                          }
                        >
                          {STATUS_LABEL[row.status]}
                        </span>
                        {row.status !== "paid" && row.status !== "voided" && (
                          <span
                            className={
                              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold " +
                              BUCKET_PILL_CLASS[row.bucket]
                            }
                          >
                            {BUCKET_LABEL[row.bucket]}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {hasMore && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => void loadMore()}
              className="rounded-md border border-cream-300 bg-white px-6 py-2.5 text-sm font-semibold text-brown-500 shadow-sm transition-colors hover:border-brown-300"
            >
              Load more
            </button>
          </div>
        )}
    </PortalLayout>
  );
}
